import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDebouncedValue } from "@/lib/Hooks/useDebouncedValue";
import { proDriverAgreementPreviewQuery } from "@/app/ProDrivers/Agreements/GraphQL/agreementQueries";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { useState, type Ref } from "react";
import { useElementSize } from "@/lib/Hooks/useElementSize";

/** A4 at Chromium's 96dpi — the page Browsershot prints. */
const A4_WIDTH_PX = 794;

type BodyFormValues = { body?: string | null; title?: string | null };

type Props = {
  title: string;
  agreementId?: string | null;
  /** When set the generated PDF is shown instead of the live render. */
  pdfUrl?: string | null;
  iframeRef?: Ref<HTMLIFrameElement>;
};

/**
 * Renders through the backend so the preview is the same blade the PDF is
 * printed from — letterhead, fonts and spacing included.
 */
export function AgreementPreview({
  title,
  agreementId,
  pdfUrl,
  iframeRef,
}: Props) {
  const { values } = useFormState<BodyFormValues>();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { width, height } = useElementSize(container);

  const input = useDebouncedValue(
    {
      title: values.title || title,
      body: values.body ?? "",
      agreement_id: agreementId ?? null,
    },
    400,
  );

  const { data, isFetching, isError } = useGqlQuery(
    proDriverAgreementPreviewQuery,
    { input },
    { enabled: !pdfUrl, placeholderData: (previous: unknown) => previous },
  );

  if (pdfUrl) {
    return (
      <iframe
        ref={iframeRef}
        src={pdfUrl}
        title="Agreement PDF"
        className="h-full w-full bg-white"
      />
    );
  }

  const html = data?.proDriverAgreementPreview;

  if (!html) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-50 p-4 text-center text-sm text-zinc-500">
        {isError ? "The preview could not be rendered." : <Spinner />}
      </div>
    );
  }

  // The render is a fixed A4 sheet, so it is scaled down to whatever width the
  // pane has rather than reflowed — the preview stays true to the printed page.
  const scale = width ? Math.min(width / A4_WIDTH_PX, 1) : 1;

  return (
    <div
      ref={setContainer}
      className="relative h-full overflow-hidden bg-zinc-200"
    >
      {isFetching ? (
        <div className="absolute right-3 top-3 z-10 rounded bg-white/80 px-2 py-1 text-xs text-zinc-500 shadow-sm">
          Updating…
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        srcDoc={html}
        title="Agreement preview"
        sandbox="allow-same-origin"
        style={{
          width: A4_WIDTH_PX,
          height: height ? height / scale : "100%",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          marginLeft: Math.max((width - A4_WIDTH_PX * scale) / 2, 0),
        }}
      />
    </div>
  );
}
